import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import MapGL, { LngLat, useMap } from "react-map-gl";

import { SurfSpot, Buoy, User, DraggableSurfer } from "../../Icons";
import Context from "../context";
import LocContext from "../LocContext";
import { MapControls, BuoyInfoPopup, SurfSpotInfoPopup, BuoyIndexSlider } from "./MapComponents";
import * as turf from "@turf/turf";

// import BuoyInfo from "./MapComponents/BuoyInfo";
import { borderColor } from "@mui/system";
import API from "../API";

function makeRadarTileUrl(minutesBack) {
    // Round to 5-minute intervals so the same URL is reused across renders,
    // enabling browser cache hits (304s). NOAA snaps to nearest scan anyway.
    const now = Math.floor(Date.now() / (5 * 60 * 1000)) * (5 * 60 * 1000);
    const time = new Date(now - minutesBack * 60 * 1000).toISOString();
    return `https://nowcoast.noaa.gov/geoserver/observations/weather_radar/ows?SERVICE=WMS&REQUEST=GetMap&VERSION=1.1.1&LAYERS=hawaii_base_reflectivity_mosaic&STYLES=&FORMAT=image/png&TRANSPARENT=true&WIDTH=256&HEIGHT=256&SRS=EPSG:3857&BBOX={bbox-epsg-3857}&TIME=${time}`;
}

// Must be a child of <MapGL> so useMap() resolves to the correct map instance.
// Uses a ping-pong (a/b) approach for single-frame changes.
// When looping, pre-creates all frame layers and toggles visibility via opacity.
const LOOP_FRAMES = [25, 20, 15, 10, 5, 0];

const RadarLayer = React.memo(function RadarLayer({ tileUrl, opacity, looping, activeFrame }) {
    const { current: mapRef } = useMap();
    const activeSlotRef = useRef("a");
    const loopLayersRef = useRef(false); // tracks whether loop layers are created

    // --- Loop mode: pre-create all 6 layers, toggle opacity to switch ---
    useEffect(() => {
        if (!looping || !mapRef) return;
        const map = mapRef.getMap();

        const setup = () => {
            // Remove any single-frame ping-pong layers
            for (const slot of ["a", "b"]) {
                if (map.getLayer(`radar-layer-${slot}`)) map.removeLayer(`radar-layer-${slot}`);
                if (map.getSource(`radar-${slot}`)) map.removeSource(`radar-${slot}`);
            }
            activeSlotRef.current = "a";

            // Create all loop frame layers (hidden initially)
            LOOP_FRAMES.forEach((mb, i) => {
                const srcId = `radar-loop-${i}`;
                const layerId = `radar-loop-layer-${i}`;
                if (map.getSource(srcId)) return; // already exists
                map.addSource(srcId, { type: "raster", tiles: [makeRadarTileUrl(mb)], tileSize: 256 });
                map.addLayer({
                    id: layerId,
                    type: "raster",
                    source: srcId,
                    paint: { "raster-opacity": 0, "raster-fade-duration": 0 },
                });
            });
            loopLayersRef.current = true;
        };

        if (map.isStyleLoaded()) setup();
        else map.once("style.load", setup);

        return () => {
            // Tear down all loop layers when looping stops
            LOOP_FRAMES.forEach((_, i) => {
                const layerId = `radar-loop-layer-${i}`;
                const srcId = `radar-loop-${i}`;
                if (map.getLayer(layerId)) map.removeLayer(layerId);
                if (map.getSource(srcId)) map.removeSource(srcId);
            });
            loopLayersRef.current = false;
        };
    }, [looping, mapRef]);

    // Toggle which loop frame is visible
    useEffect(() => {
        if (!looping || !mapRef || !loopLayersRef.current) return;
        const map = mapRef.getMap();
        LOOP_FRAMES.forEach((_, i) => {
            const layerId = `radar-loop-layer-${i}`;
            if (!map.getLayer(layerId)) return;
            map.setPaintProperty(layerId, "raster-opacity", i === activeFrame ? opacity : 0);
        });
    }, [activeFrame, opacity, looping, mapRef]);

    // --- Single-frame mode: ping-pong swap ---
    useEffect(() => {
        if (looping || !mapRef) return;
        const map = mapRef.getMap();

        const activeSlot = activeSlotRef.current;
        const nextSlot = activeSlot === "a" ? "b" : "a";
        const activeSrc = `radar-${activeSlot}`;
        const activeLayer = `radar-layer-${activeSlot}`;
        const nextSrc = `radar-${nextSlot}`;
        const nextLayer = `radar-layer-${nextSlot}`;

        let onData = null;

        const apply = () => {
            if (map.getLayer(nextLayer)) map.removeLayer(nextLayer);
            if (map.getSource(nextSrc)) map.removeSource(nextSrc);

            map.addSource(nextSrc, { type: "raster", tiles: [tileUrl], tileSize: 256 });
            map.addLayer({
                id: nextLayer,
                type: "raster",
                source: nextSrc,
                paint: { "raster-opacity": opacity, "raster-fade-duration": 300 },
            });

            let sawTile = false;
            onData = (e) => {
                if (e.sourceId !== nextSrc) return;
                if (e.tile) sawTile = true;
                if (!sawTile || !e.isSourceLoaded) return;
                map.off("sourcedata", onData);
                onData = null;
                if (map.getLayer(activeLayer)) map.removeLayer(activeLayer);
                if (map.getSource(activeSrc)) map.removeSource(activeSrc);
                activeSlotRef.current = nextSlot;
            };

            map.on("sourcedata", onData);
        };

        if (map.isStyleLoaded()) apply();
        else map.once("style.load", apply);

        return () => {
            if (onData) map.off("sourcedata", onData);
            if (activeSlotRef.current !== nextSlot) {
                if (map.getLayer(nextLayer)) map.removeLayer(nextLayer);
                if (map.getSource(nextSrc)) map.removeSource(nextSrc);
            }
        };
    }, [tileUrl, looping, mapRef]);

    // Opacity-only changes for single-frame mode
    useEffect(() => {
        if (looping || !mapRef) return;
        const map = mapRef.getMap();
        const activeLayer = `radar-layer-${activeSlotRef.current}`;
        if (!map.getLayer(activeLayer)) return;
        map.setPaintProperty(activeLayer, "raster-opacity", opacity);
    }, [opacity, looping, mapRef]);

    return null;
});

export default function Map() {
    const [popupInfo, setBuoyInfo] = useState(null);
    const [surfSpotInfo, setSurfSpotInfo] = useState(null);
    const [replayEnabled, setReplayEnabled] = useState(false);
    const [buoysInView, setBuoysInView] = useState(0);
    const [loadingReplay, setLoadingReplay] = useState(false);
    const [replayBuoyIds, setReplayBuoyIds] = useState([]); // Track which buoys have full data
    const [radarOpacity, setRadarOpacity] = useState(0.6);
    const [radarMinutesBack, setRadarMinutesBack] = useState(0);
    // const [radarIndex, setRadarIndex] = useState(0);
    const [radarLooping, setRadarLooping] = useState(false);
    const [radarLoopFrame, setRadarLoopFrame] = useState(0);
    const radarLoopRef = useRef(null);
    // const [circleSource, setCircleSource] = useState(true);
    const {
        addSurfSpot,
        buoyIndex,
        buoyMarkers,
        setBuoyMarkers,
        groupLocations,
        mapMode,
        setAddSurfSpot,
        setBuoyIndex,
        surfSpots,
        userLoc,
        replayTime,
        setReplayTime,
        replayTimeRange,
        setReplayTimeRange,
        showSurfSpots,
    } = useContext(Context);
    const { current: map } = useMap();

    const { loc } = useContext(LocContext);

    const [viewport, setViewport] = useState({
        // latitude: 37.8,
        // longitude: -122.4,
        zoom: 5,
        bearing: 0,
        pitch: 0,
    });

    //triggers when map mode switches
    useEffect(() => {
        setBuoyInfo(null);
    }, [mapMode]);

    //boolean that triggers when setting a surf spot
    useEffect(() => {
        if (!addSurfSpot) return;

        console.log(viewport);

        // setViewport({
        //     ...viewport,
        //     zoom: 14,
        // });
    }, [addSurfSpot]);

    //Updating location of map
    useEffect(() => {
        if (!loc) return;

        setViewport({
            ...viewport,
            latitude: loc.latitude,
            longitude: loc.longitude,
        });
    }, [loc]);

    // Count buoys in viewport when location loads or map moves
    useEffect(() => {
        if (!userLoc || !buoyMarkers || Object.keys(buoyMarkers).length === 0) {
            setBuoysInView(0);
            return;
        }

        // If map is not ready yet, use viewport bounds as fallback
        let bounds;
        if (map) {
            bounds = map.getBounds();
        } else if (viewport.latitude && viewport.longitude) {
            // Estimate bounds from viewport (rough approximation)
            const latDelta = 5 / Math.pow(2, viewport.zoom - 5);
            const lngDelta = 10 / Math.pow(2, viewport.zoom - 5);
            bounds = {
                getWest: () => viewport.longitude - lngDelta,
                getEast: () => viewport.longitude + lngDelta,
                getSouth: () => viewport.latitude - latDelta,
                getNorth: () => viewport.latitude + latDelta,
            };
        }

        if (!bounds) {
            setBuoysInView(0);
            return;
        }

        // Count buoys within viewport
        let count = 0;
        Object.keys(buoyMarkers).forEach((buoyId) => {
            const buoyData = buoyMarkers[buoyId];
            if (!buoyData || buoyData.length === 0) return;

            const lastData = buoyData[buoyData.length - 1];
            if (!lastData?.coords?.coordinates) return;

            const [lng, lat] = lastData.coords.coordinates;

            // Check if buoy is within bounds
            if (lng >= bounds.getWest() && lng <= bounds.getEast() && lat >= bounds.getSouth() && lat <= bounds.getNorth()) {
                // Also check if it passes the current mapMode filter
                if (checkMapMode(buoyData, mapMode)) {
                    count++;
                }
            }
        });

        setBuoysInView(count);
    }, [userLoc, viewport, buoyMarkers, mapMode, map]);

    // Function to center map on specific coordinates
    const centerOnLocation = useCallback((latitude, longitude, zoom) => {
        console.log("Centering on:", { latitude, longitude });
        setViewport((prevViewport) => ({
            ...prevViewport,
            latitude,
            longitude,
            // Only update zoom if explicitly provided
            ...(zoom != null && { zoom }),
        }));
    }, []);

    // Helper function to find closest data point to a given time
    const findClosestDataPoint = useCallback((buoyData, targetTime) => {
        if (!buoyData || buoyData.length === 0) return null;
        if (!targetTime) return buoyData[buoyData.length - 1]; // Return latest if no target time

        let closest = buoyData[0];
        let minDiff = Math.abs(new Date(closest.GMT).getTime() - targetTime);

        for (let i = 1; i < buoyData.length; i++) {
            const point = buoyData[i];
            const diff = Math.abs(new Date(point.GMT).getTime() - targetTime);
            if (diff < minDiff) {
                minDiff = diff;
                closest = point;
            }
        }

        return closest;
    }, []);

    // Handle enabling replay mode
    const handleEnableReplay = useCallback(async () => {
        setLoadingReplay(true);
        console.log(`Enabling replay for ${buoysInView} buoys in view`);

        // Get buoy IDs currently in viewport
        const buoyIdsInView = [];
        let bounds;

        if (map) {
            bounds = map.getBounds();
        } else if (viewport.latitude && viewport.longitude) {
            const latDelta = 5 / Math.pow(2, viewport.zoom - 5);
            const lngDelta = 10 / Math.pow(2, viewport.zoom - 5);
            bounds = {
                getWest: () => viewport.longitude - lngDelta,
                getEast: () => viewport.longitude + lngDelta,
                getSouth: () => viewport.latitude - latDelta,
                getNorth: () => viewport.latitude + latDelta,
            };
        }

        if (bounds) {
            Object.keys(buoyMarkers).forEach((buoyId) => {
                const buoyData = buoyMarkers[buoyId];
                if (!buoyData || buoyData.length === 0) return;

                const lastData = buoyData[buoyData.length - 1];
                if (!lastData?.coords?.coordinates) return;

                const [lng, lat] = lastData.coords.coordinates;

                if (lng >= bounds.getWest() && lng <= bounds.getEast() && lat >= bounds.getSouth() && lat <= bounds.getNorth()) {
                    if (checkMapMode(buoyData, mapMode)) {
                        buoyIdsInView.push(buoyId);
                    }
                }
            });
        }

        console.log(`Fetching full data for ${buoyIdsInView.length} buoys:`, buoyIdsInView);

        // Fetch full 24-hour data for each buoy in view
        const fetchPromises = buoyIdsInView.map(async (buoyId) => {
            try {
                const data = await API.getBuoy(buoyId);
                return { buoyId, data: data[buoyId] };
            } catch (err) {
                console.error(`Failed to fetch data for buoy ${buoyId}:`, err);
                return null;
            }
        });

        const results = await Promise.all(fetchPromises);

        // Update buoyMarkers with full historical data
        const updatedMarkers = { ...buoyMarkers };
        results.forEach((result) => {
            if (result && result.data) {
                // Preserve coordinates from original data
                const originalCoords = updatedMarkers[result.buoyId]?.[0]?.coords;

                let filteredData = result.data;

                // For swell mode, filter for data with swellDir if any exists
                if (mapMode === "swell") {
                    const dataWithDir = result.data.filter((d) => d.swellDir != null);
                    if (dataWithDir.length > 0) {
                        filteredData = dataWithDir;
                        console.log(`Buoy ${result.buoyId}: filtered from ${result.data.length} to ${filteredData.length} points with swellDir`);
                    }
                }

                // Add coordinates to all data points
                if (originalCoords) {
                    filteredData.forEach((d) => {
                        if (!d.coords) {
                            d.coords = originalCoords;
                        }
                    });
                }

                updatedMarkers[result.buoyId] = filteredData;
                console.log(`Updated buoy ${result.buoyId} with ${filteredData.length} data points`);
            }
        });

        // Calculate time range across all loaded buoys
        let minTime = Infinity;
        let maxTime = -Infinity;

        buoyIdsInView.forEach((buoyId) => {
            const data = updatedMarkers[buoyId];
            if (data && data.length > 0) {
                data.forEach((point) => {
                    const time = new Date(point.GMT).getTime();
                    if (time < minTime) minTime = time;
                    if (time > maxTime) maxTime = time;
                });
            }
        });

        console.log("Time range:", new Date(minTime).toISOString(), "to", new Date(maxTime).toISOString());

        // Update state with full historical data
        console.log("Full historical data loaded for buoys:", buoyIdsInView);
        setBuoyMarkers(updatedMarkers);
        setReplayBuoyIds(buoyIdsInView);
        setReplayTimeRange({ min: minTime, max: maxTime });
        setReplayTime(maxTime); // Start at most recent time
        setLoadingReplay(false);
        setReplayEnabled(true);
    }, [buoysInView, buoyMarkers, setBuoyMarkers, map, viewport, mapMode, setReplayTimeRange, setReplayTime]);

    const handleExitReplay = useCallback(() => {
        setReplayEnabled(false);
        setReplayTime(null);
        setReplayBuoyIds([]);
        setReplayTimeRange({ min: null, max: null });
    }, [setReplayTime, setReplayTimeRange]);

    // filledMarkers: only re-runs when underlying data changes, NOT on every slider tick
    const filledMarkers = useMemo(() => {
        const allBuoys = {};
        const buoysToProcess = replayEnabled && replayBuoyIds.length > 0 ? replayBuoyIds : Object.keys(buoyMarkers);

        console.log("fillGaps - processing", buoysToProcess.length, "buoys", replayEnabled ? "(replay mode)" : "(normal mode)");

        buoysToProcess.forEach((buoyId) => {
            const buoyData = buoyMarkers[buoyId];
            if (!buoyData) return;
            // console.log(`Buoy ${buoyId} has ${buoyData?.length} data points`);
            if (checkMapMode(buoyData, mapMode)) allBuoys[buoyId] = buoyData;
        });

        return fillGaps(allBuoys, mapMode);
    }, [buoyMarkers, mapMode, replayEnabled, replayBuoyIds]);

    // Buoys: re-renders icons when time/index changes, but fillGaps already done
    const Buoys = useMemo(() => {
        return Object.keys(filledMarkers).map((buoyId) => {
            const buoyData = filledMarkers[buoyId];
            if (!checkMapMode(buoyData, mapMode)) return <React.Fragment key={buoyId}></React.Fragment>;
            return (
                <Buoy
                    key={buoyId}
                    mapMode={mapMode}
                    onClick={async () => {
                        const buoy = replayEnabled && replayTime ? findClosestDataPoint(buoyData, replayTime) : buoyData.slice(-buoyIndex)[0];
                        const [longitude, latitude] = buoy?.coords?.coordinates;
                        centerOnLocation(latitude - 2, longitude);
                        setBuoyInfo(buoyData);
                        const data = await API.getBuoy(buoyId);
                        data[buoyId].forEach((d) => {
                            d.coords = { coordinates: [longitude, latitude] };
                        });
                        setBuoyInfo(data[buoyId]);
                    }}
                    buoyData={buoyData}
                    buoyIndex={buoyIndex}
                    replayEnabled={replayEnabled}
                    replayTime={replayTime}
                    findClosestDataPoint={findClosestDataPoint}
                />
            );
        });
    }, [filledMarkers, mapMode, buoyIndex, centerOnLocation, replayEnabled, replayTime, findClosestDataPoint]);

    const PopWindow = useMemo(() => {
        return <BuoyInfoPopup setInfo={setBuoyInfo} info={popupInfo} />;
    }, [popupInfo]);

    const SurfSpotPopup = useMemo(() => {
        if (!surfSpotInfo) return null;
        return <SurfSpotInfoPopup setInfo={setSurfSpotInfo} info={surfSpotInfo} />;
    }, [surfSpotInfo]);

    // Radar loop: cycles through LOOP_FRAMES by index, no source create/destroy
    useEffect(() => {
        if (!radarLooping) {
            clearInterval(radarLoopRef.current);
            radarLoopRef.current = null;
            return;
        }
        setRadarLoopFrame(0);
        setRadarMinutesBack(LOOP_FRAMES[0]);
        radarLoopRef.current = setInterval(() => {
            setRadarLoopFrame((prev) => {
                const next = (prev + 1) % LOOP_FRAMES.length;
                setRadarMinutesBack(LOOP_FRAMES[next]);
                return next;
            });
        }, 1500);
        return () => clearInterval(radarLoopRef.current);
    }, [radarLooping]);

    const radarTileUrl = useMemo(() => makeRadarTileUrl(radarMinutesBack), [radarMinutesBack]);
    const radarTime = useMemo(() => new Date(Date.now() - radarMinutesBack * 60 * 1000).toISOString(), [radarMinutesBack]);

    // Manage radar source/layer entirely via Mapbox GL API.

    return (
        <MapGL
            {...viewport}
            width="100%"
            height="100%"
            mapStyle="mapbox://styles/mapbox/satellite-streets-v11"
            onMove={(evt) => setViewport(evt.viewState)}
            mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
            onClick={(e) => {
                // if (popupInfo) {
                //     setBuoyInfo(null);
                // }
                if (e.lngLat) {
                    console.log({ lat: e.lngLat.lat, lng: e.lngLat.lng });
                }
            }}
        >
            <RadarLayer tileUrl={radarTileUrl} opacity={radarOpacity} looping={radarLooping} activeFrame={radarLoopFrame} />

            {/* Radar controls box */}
            <div
                style={{
                    position: "absolute",
                    bottom: 80,
                    right: 10,
                    background: "rgba(0,0,0,0.7)",
                    borderRadius: 6,
                    padding: "8px 12px",
                    zIndex: 10,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    minWidth: 260,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: "#fff", fontSize: 11, fontWeight: "bold" }}>Radar</span>
                    <button
                        onClick={() => setRadarLooping((v) => !v)}
                        style={{
                            background: radarLooping ? "#4af" : "rgba(255,255,255,0.15)",
                            color: "#fff",
                            border: "none",
                            borderRadius: 4,
                            padding: "2px 8px",
                            fontSize: 10,
                            cursor: "pointer",
                            fontWeight: radarLooping ? "bold" : "normal",
                        }}
                        title="Loop last 25 minutes of radar"
                    >
                        {radarLooping ? "⏸ Loop" : "▶ Loop"}
                    </button>
                </div>
                <span style={{ color: "#adf", fontSize: 12, whiteSpace: "nowrap" }}>
                    {(() => {
                        const d = new Date(radarTime);
                        return isNaN(d) ? radarTime : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                    })()}
                </span>
                <input
                    type="range"
                    min={0}
                    max={440}
                    step={5}
                    value={radarMinutesBack}
                    onChange={(e) => {
                        setRadarLooping(false);
                        setRadarMinutesBack(parseInt(e.target.value));
                        // setRadarIndex(parseInt(e.target.value));
                    }}
                    style={{ width: 240, cursor: "pointer" }}
                    title="Radar time (minutes back)"
                />
                <span style={{ color: "#aaa", fontSize: 10 }}>Opacity {Math.round(radarOpacity * 100)}%</span>
                <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={radarOpacity}
                    onChange={(e) => setRadarOpacity(parseFloat(e.target.value))}
                    style={{ width: 240, cursor: "pointer" }}
                    title="Radar opacity"
                />
            </div>

            {/* <Source id="my-data" type="geojson" data={_circle}>
                <Layer {...layerStyle} />
            </Source> */}
            {/* {circleSource && circleSource.map((c) => c)} */}
            <BuoyIndexSlider
                filledMarkers={filledMarkers}
                replayEnabled={replayEnabled}
                onEnableReplay={handleEnableReplay}
                onExitReplay={handleExitReplay}
                buoysInView={buoysInView}
                loading={loadingReplay}
            />
            {userLoc && <User width={20} height={20} {...userLoc} />}
            {/* BUOYS */}
            {Buoys}

            {showSurfSpots &&
                surfSpots?.length > 0 &&
                surfSpots.map((surfSpot) => {
                    return (
                        <SurfSpot
                            onClick={() => {
                                console.log(surfSpot);
                                setSurfSpotInfo(surfSpot);
                            }}
                            key={surfSpot._id}
                            surfSpot={surfSpot}
                        />
                    );
                })}

            {popupInfo && PopWindow}

            {/* Surf spot popup */}
            {SurfSpotPopup}

            {addSurfSpot && <DraggableSurfer />}

            <MapControls />
        </MapGL>
    );
}

const dataPerMapMode = {
    swell: ["height", "period", "swellDir"],
    wind: ["windDir", "windGust", "windSpeed"],
    temperature: ["airTemp", "waterTemp"],
};
function checkMapMode(buoyData, mapMode) {
    // console.log(buoyData, mapMode);
    const data = buoyData[buoyData.length - 1];
    if (mapMode === "swell") {
        let { height, period, swellDir } = data;
        // Check that height and period exist (swellDir is optional)
        if (height != null && period != null) {
            return true;
        }
    } else if (mapMode === "wind") {
        const { windDir, windGust, windSpeed } = data;
        // Check that all required fields exist and are not null/undefined
        if (windDir != null && windGust != null && windSpeed != null) {
            return true;
        }
    } else if (mapMode === "temperature") {
        const { airTemp, waterTemp } = data;
        // Check that all required fields exist and are not null/undefined
        if (airTemp != null && waterTemp != null) {
            return true;
        }
    } else if (mapMode === "pressure") {
        const { pressureTendency, pressure } = data;
        // Check that all required fields exist and are not null/undefined
        if (pressureTendency != null && pressure != null) {
            return true;
        }
    } else if (mapMode === "salinity") {
        const { salinity } = data;
        // Check that field exists and is not null/undefined
        if (salinity != null) {
            return true;
        }
    } else if (mapMode === "visibility") {
        const { visibility } = data;
        // Check that field exists and is not null/undefined
        if (visibility != null) {
            return true;
        }
    } else {
        throw new Error("Whats the mapMode@?@?!?");
    }
    return false;
}

function fillGaps(buoyMarkers, mapMode) {
    let longest;
    let timestamps = [];
    //find the longest one?
    Object.keys(buoyMarkers).forEach((buoyId) => {
        const data = buoyMarkers[buoyId];
        if (!longest) {
            longest = data.length;
            timestamps = data.map(({ GMT }) => GMT);
        } else if (data.length > longest) {
            longest = data.length;
            timestamps = data.map(({ GMT }) => GMT);
        }
    });

    // console.log({ longest, timestamps });

    Object.keys(buoyMarkers).forEach((buoyId) => {
        const buoyHistory = buoyMarkers[buoyId];
        // console.log(buoyHistory);
        const finalBuoyData = [];

        // console.log("Starting length ", buoyHistory.length);

        timestamps.forEach((GMT, i) => {
            //does this data history have this timestamp?
            const found = buoyHistory.find((data) => data.GMT === GMT);
            if (!found) {
                // console.log("NO we neeeed this");
                if (i === 0) {
                    return;
                }
                const prevGMT = timestamps[i - 1];
                const prevData = buoyHistory.find((d) => d.GMT === prevGMT);

                if (!prevData) {
                    return;
                }
                finalBuoyData.push({ lerp: true, GMT, ...prevData });
            } else {
                // console.log("yes we have this");
            }
        });

        // console.log("added data ", finalBuoyData.length);
        buoyHistory.push(...finalBuoyData);
        // console.log("Final length ", buoyHistory.length);

        buoyHistory.sort((a, b) => new Date(a.GMT).getTime() - new Date(b.GMT).getTime());

        // const dataKeys = dataPerMapMode[mapMode];

        // dataKeys?.forEach((dataKey) => {
        //     //look for all the data is there
        //     buoyHistory.forEach((data, dataI) => {
        //         //so we have a data?
        //         const currentData = data[dataKey];

        //         if (currentData) {
        //             // console.log(`Got ${currentData} for ${dataKey}`);
        //         } else {
        //             // console.log(`NO ! Got ${currentData} for ${dataKey}`);
        //         }
        //     });
        // });
    });

    return buoyMarkers;
}
